import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowLeft,
  faArrowUp,
  faListCheck,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import api from "../api/API";
import { useWorkspace } from "../context/WorkspaceContext";
import {
  atualizarModelo,
  buscarModelo,
  criarModelo,
  listarModelos,
  removerModelo,
} from "../api/taskTemplatesApi";
import { TASK_PRIORITY, TASK_PRIORITY_OPTIONS } from "../utils/taskPriority";
import "./ModelosTarefaPage.css";

const MODO = { LIVRE: 0, SEQUENCIAL: 1 };

const itemVazio = () => ({
  descricao: "",
  dias_offset: "",
  prioridade: TASK_PRIORITY.MEDIUM,
  id_usuario_responsavel: "",
});

const ModelosTarefaPage = () => {
  const navigate = useNavigate();
  const { selectedWorkspaceId } = useWorkspace();

  const [modelos, setModelos] = useState([]);
  const [membros, setMembros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  const [editando, setEditando] = useState(null);

  const carregar = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setModelos([]);
      setCarregando(false);
      return;
    }

    setCarregando(true);
    setErro("");

    try {
      setModelos(await listarModelos(selectedWorkspaceId));

      const resposta = await api
        .get(`/workspaces/${selectedWorkspaceId}/members`)
        .catch(() => null);

      const lista = Array.isArray(resposta?.data)
        ? resposta.data
        : resposta?.data?.records || [];

      setMembros(
        lista.map((membro) => ({
          id: String(membro?.id_usuario ?? membro?.user_id ?? membro?.id ?? ""),
          nome: membro?.descricao || membro?.nome || membro?.email || "Sem nome",
        }))
      );
    } catch (error) {
      setErro(error?.message || "Nao foi possivel carregar os modelos.");
    } finally {
      setCarregando(false);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function novoModelo() {
    setEditando({
      id: null,
      nome: "",
      descricao: "",
      modo_checklist: MODO.LIVRE,
      itens: [itemVazio()],
    });
  }

  async function abrirModelo(id) {
    setErro("");
    try {
      const registro = await buscarModelo(id);
      setEditando({
        ...registro,
        itens: (registro.itens || []).map((item) => ({
          ...item,
          dias_offset: item.dias_offset ?? "",
          id_usuario_responsavel: item.id_usuario_responsavel || "",
        })),
      });
    } catch (error) {
      setErro("Nao foi possivel abrir o modelo.");
    }
  }

  function atualizarItem(indice, campo, valor) {
    setEditando((atual) => ({
      ...atual,
      itens: atual.itens.map((item, i) => (i === indice ? { ...item, [campo]: valor } : item)),
    }));
  }

  function moverItem(indice, direcao) {
    setEditando((atual) => {
      const destino = indice + direcao;
      if (destino < 0 || destino >= atual.itens.length) return atual;

      const itens = [...atual.itens];
      [itens[indice], itens[destino]] = [itens[destino], itens[indice]];
      return { ...atual, itens };
    });
  }

  async function salvar() {
    if (!editando?.nome?.trim()) {
      setErro("O modelo precisa de um nome.");
      return;
    }

    setSalvando(true);
    setErro("");

    const payload = {
      nome: editando.nome.trim(),
      descricao: editando.descricao || null,
      modo_checklist: Number(editando.modo_checklist) || MODO.LIVRE,
      itens: editando.itens.filter((item) => item.descricao.trim()),
    };

    try {
      if (editando.id) await atualizarModelo(editando.id, payload);
      else await criarModelo(selectedWorkspaceId, payload);

      setEditando(null);
      await carregar();
    } catch (error) {
      setErro(error?.response?.data?.message || "Nao foi possivel salvar o modelo.");
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(modelo) {
    if (!window.confirm(`Remover o modelo "${modelo.nome}"?`)) return;

    try {
      await removerModelo(modelo.id);
      await carregar();
    } catch (error) {
      setErro("Nao foi possivel remover o modelo.");
    }
  }

  if (!selectedWorkspaceId) {
    return (
      <div className="mt-page">
        <Alert variant="info">Selecione um workspace para gerenciar modelos.</Alert>
      </div>
    );
  }

  return (
    <div className="mt-page">
      <header className="mt-header">
        <div>
          <button type="button" className="mt-voltar" onClick={() => navigate("/tarefas")}>
            <FontAwesomeIcon icon={faArrowLeft} /> Tarefas
          </button>
          <h4>
            <FontAwesomeIcon icon={faListCheck} /> Modelos de checklist
          </h4>
          <small>
            A sequência de passos que você repete a cada cliente. Monte uma vez, aplique sempre.
          </small>
        </div>

        {!editando && (
          <Button onClick={novoModelo}>
            <FontAwesomeIcon icon={faPlus} /> Novo modelo
          </Button>
        )}
      </header>

      {erro && <Alert variant="danger">{erro}</Alert>}

      {carregando ? (
        <div className="mt-carregando"><Spinner size="sm" /></div>
      ) : editando ? (
        <div className="mt-editor">
          <div className="mt-editor-topo">
            <Form.Group className="flex-grow-1">
              <Form.Label>Nome do modelo</Form.Label>
              <Form.Control
                type="text"
                value={editando.nome}
                autoFocus
                placeholder="Ex: Implantacao de sistema"
                onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Execução</Form.Label>
              <Form.Select
                value={editando.modo_checklist}
                onChange={(e) =>
                  setEditando({ ...editando, modo_checklist: Number(e.target.value) })
                }
              >
                <option value={MODO.LIVRE}>Passos em qualquer ordem</option>
                <option value={MODO.SEQUENCIAL}>Sequencial</option>
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group className="mb-4">
            <Form.Label>Descrição (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editando.descricao || ""}
              onChange={(e) => setEditando({ ...editando, descricao: e.target.value })}
            />
          </Form.Group>

          <div className="mt-passos-head">
            <h5>Passos</h5>
            <span>
              <strong>D+N</strong> conta os dias a partir da data de início informada na
              aplicação — é o que faz o modelo servir para o próximo cliente sem edição.
            </span>
          </div>

          <div className="mt-passos">
            {editando.itens.map((item, indice) => (
              <div key={indice} className="mt-passo">
                <span className="mt-passo-num">{indice + 1}</span>

                <Form.Control
                  className="mt-passo-desc"
                  type="text"
                  value={item.descricao}
                  placeholder="O que precisa ser feito"
                  onChange={(e) => atualizarItem(indice, "descricao", e.target.value)}
                />

                <div className="mt-passo-offset">
                  <span>D+</span>
                  <Form.Control
                    type="number"
                    min="0"
                    value={item.dias_offset}
                    placeholder="-"
                    onChange={(e) => atualizarItem(indice, "dias_offset", e.target.value)}
                  />
                </div>

                <Form.Select
                  className="mt-passo-prio"
                  value={item.prioridade}
                  onChange={(e) => atualizarItem(indice, "prioridade", Number(e.target.value))}
                >
                  {TASK_PRIORITY_OPTIONS.map((opcao) => (
                    <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
                  ))}
                </Form.Select>

                <Form.Select
                  className="mt-passo-dono"
                  value={item.id_usuario_responsavel}
                  onChange={(e) =>
                    atualizarItem(indice, "id_usuario_responsavel", e.target.value)
                  }
                >
                  <option value="">Sem dono fixo</option>
                  {membros.map((membro) => (
                    <option key={membro.id} value={membro.id}>{membro.nome}</option>
                  ))}
                </Form.Select>

                <div className="mt-passo-acoes">
                  <button type="button" onClick={() => moverItem(indice, -1)} title="Subir">
                    <FontAwesomeIcon icon={faArrowUp} />
                  </button>
                  <button type="button" onClick={() => moverItem(indice, 1)} title="Descer">
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                  <button
                    type="button"
                    className="remover"
                    title="Remover passo"
                    onClick={() =>
                      setEditando({
                        ...editando,
                        itens: editando.itens.filter((_, i) => i !== indice),
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline-secondary"
            className="mt-add"
            onClick={() => setEditando({ ...editando, itens: [...editando.itens, itemVazio()] })}
          >
            <FontAwesomeIcon icon={faPlus} /> Adicionar passo
          </Button>

          <div className="mt-editor-rodape">
            <Button variant="secondary" onClick={() => setEditando(null)} disabled={salvando}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar modelo"}
            </Button>
          </div>
        </div>
      ) : modelos.length === 0 ? (
        <div className="mt-vazio">
          <FontAwesomeIcon icon={faListCheck} />
          <strong>Nenhum modelo ainda.</strong>
          <span>
            Se você repete os mesmos passos a cada implantação, este é o lugar de escrevê-los
            uma vez só.
          </span>
          <Button onClick={novoModelo}>Criar o primeiro</Button>
        </div>
      ) : (
        <div className="mt-lista">
          {modelos.map((modelo) => (
            <article key={modelo.id} className="mt-card" onClick={() => abrirModelo(modelo.id)}>
              <div>
                <strong>{modelo.nome}</strong>
                {modelo.descricao && <p>{modelo.descricao}</p>}
                <small>
                  {modelo.total_itens} passo(s)
                  {Number(modelo.modo_checklist) === MODO.SEQUENCIAL && " · sequencial"}
                </small>
              </div>

              <button
                type="button"
                className="mt-card-excluir"
                title="Remover modelo"
                onClick={(evento) => {
                  evento.stopPropagation();
                  excluir(modelo);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelosTarefaPage;
